import React, { useState, useEffect } from 'react';
import { materialesAPI, ingredientesAPI, ubicacionesAPI, proveedoresAPI } from '../api/api';
import { Plus, Search, Edit, Trash2, Package, X, Truck, Bell, AlertTriangle } from 'lucide-react';
import Vehiculos from './Vehiculos';

// Secciones del catálogo de alquiler (las del catálogo del proveedor)
const SECCIONES_ALQUILER = [
  'Vajilla', 'Cristalería', 'Cubertería', 'Mantelería', 'Mobiliario', 'Maquinaria', 'Servilletas',
  'Menaje de servicio', 'Miniaturas', 'Menaje de cocina', 'Bandejas', 'Coctelería', 'Isotérmicos', 'Otros',
];

// Texto en minúsculas y sin acentos para el buscador
const normalizarTexto = (texto) => String(texto || '')
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const CAMPOS_BUSQUEDA = ['nombre', 'modelo_catalogo', 'medida', 'seccion_catalogo', 'referencia_proveedor', 'proveedor_alquiler_nombre'];

// Nombres del envase de carga (rack de copas, caja de servilletas...)
const NOMBRES_ENVASE = ['rack', 'caja', 'pack', 'bolsa'];

// Texto corto del envase de un material: "rack de 25" (vacío si no tiene)
const textoEnvase = (material) => {
  const uds = Number(material?.envase_uds);
  if (!uds || uds <= 1) return '';
  const udsTxt = Number.isInteger(uds) ? uds : Math.round(uds * 10) / 10;
  return `${material.envase_nombre || 'caja'} de ${udsTxt}`;
};

// 'YYYY-MM-DD' a 'dd/mm/aaaa'
const fechaCorta = (iso) => {
  if (!iso) return '';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return a && m && d ? `${d}/${m}/${a}` : '';
};

const Materiales = () => {
  const [materiales, setMateriales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [filterActivo, setFilterActivo] = useState('true');
  const [filterSeccion, setFilterSeccion] = useState('');
  const [ordenAlquiler, setOrdenAlquiler] = useState('');
  const [usoAlquiler, setUsoAlquiler] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [disponibilidadSemana, setDisponibilidadSemana] = useState({});
  const [ubicaciones, setUbicaciones] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [proveedores, setProveedores] = useState([]);
  const [showUbicacionModal, setShowUbicacionModal] = useState(false);
  const [nuevaUbicacion, setNuevaUbicacion] = useState('');
  // Todos los ingredientes (para elegir qué va dentro de una caja preparada)
  const [todosIngredientes, setTodosIngredientes] = useState([]);
  const [busquedaCajaFiltro, setBusquedaCajaFiltro] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    categoria: 'menaje',
    tipo_coste: 'inventariable',  // 'consumible', 'inventariable' o 'alquiler'
    precio_unitario: 0,
    precio_compra: 0,
    unidad_medida: 'unidad',
    formato_unidad: 'unidades',  // solo para consumibles: 'metros' o 'unidades'
    cantidad_por_envase: 1,  // cantidad en el envase
    precio_por_envase: 0,  // precio del envase completo
    stock: 0,
    stock_minimo: 0,
    ubicacion: '',
    proveedor_id: null,
    foto: '',
    notas: '',
    activo: true,
    reutilizable: true,
    es_caja: false,
    caja_ingredientes_ids: [],
    // Envase de carga (rack de 25 copas, caja de servilletas...)
    envase_uds: '',
    envase_entero: false,
    envase_nombre: 'caja',
    // Campos de alquiler
    precio_alquiler_dia1: 0,
    precio_alquiler_dia_extra: 0,
    coste_limpieza: 0,
    proveedor_alquiler_nombre: '',
    seccion_catalogo: '',
    modelo_catalogo: '',
    medida: '',
    referencia_proveedor: '',
    lavado_aparte: false,
    vuelve_si_sobra: false,
  });

  useEffect(() => {
    fetchMateriales();
    fetchUbicaciones();
    fetchProveedores();
  }, []);

  // Veces que se ha alquilado cada material (no bloquea la carga de la lista)
  useEffect(() => {
    let cancelado = false;
    materialesAPI.usoAlquiler()
      .then((res) => {
        if (!cancelado && res.data && typeof res.data === 'object') setUsoAlquiler(res.data);
      })
      .catch((error) => console.error('Error cargando el uso de alquiler:', error));
    return () => { cancelado = true; };
  }, []);

  const fetchProveedores = async () => {
    try {
      const response = await proveedoresAPI.getAll();
      setProveedores(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching proveedores:', error);
      setProveedores([]);
    }
  };

  const fetchUbicaciones = async () => {
    try {
      const response = await ubicacionesAPI.getAll(true);
      setUbicaciones(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching ubicaciones:', error);
      setUbicaciones([]);
    }
  };

  const fetchMateriales = async () => {
    try {
      // Fetch materiales regulares
      const response = await materialesAPI.getAll();
      const materialesData = Array.isArray(response.data) ? response.data : [];
      
      // Fetch ingredientes de categoría Menaje y sin categoría
      const ingredientesResponse = await ingredientesAPI.getAll();
      const ingredientesData = Array.isArray(ingredientesResponse.data) ? ingredientesResponse.data : [];
      setTodosIngredientes(ingredientesData
        .filter(ing => ing && ing.id)
        .map(ing => ({ id: ing.id, nombre: ing.nombre || '', unidad: ing.unidad || '', categoria: ing.categoria || '' })));
      const ingredientesMenaje = ingredientesData
        .filter(ing => ing.categoria === 'Menaje' || !ing.categoria || ing.categoria === '')
        .map(ing => ({
          ...ing,
          _esIngrediente: true, // Marcador para identificarlos
          _sinCategoria: !ing.categoria || ing.categoria === '' // Marcar sin categoría
        }));
      
      // Combinar materiales e ingredientes de menaje
      const todosMateriales = [...materialesData, ...ingredientesMenaje];
      setMateriales(todosMateriales);
      
      // Fetch disponibilidad semanal (una sola llamada para todos los materiales)
      try {
        const dispResponse = await materialesAPI.getDisponibilidadSemana(7);
        const dispData = Array.isArray(dispResponse.data) ? dispResponse.data : [];
        const dispMap = {};
        for (const item of dispData) {
          dispMap[item.id] = item;
        }
        setDisponibilidadSemana(dispMap);
      } catch (error) {
        console.error('Error fetching disponibilidad semanal:', error);
      }
    } catch (error) {
      console.error('Error fetching materiales:', error);
      setMateriales([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Sanitize numeric fields before submission
      const cleanData = {
        ...formData,
        precio_unitario: parseFloat(formData.precio_unitario) || 0,
        precio_compra: parseFloat(formData.precio_compra) || 0,
        stock: parseFloat(formData.stock) || 0,
        stock_minimo: parseFloat(formData.stock_minimo) || 0,
        cantidad_por_envase: parseFloat(formData.cantidad_por_envase) || 1,
        precio_por_envase: parseFloat(formData.precio_por_envase) || 0,
        foto: formData.foto || null,
        proveedor_id: formData.proveedor_id || null,
        precio_alquiler_dia1: parseFloat(formData.precio_alquiler_dia1) || 0,
        precio_alquiler_dia_extra: parseFloat(formData.precio_alquiler_dia_extra) || 0,
        coste_limpieza: parseFloat(formData.coste_limpieza) || 0,
        proveedor_alquiler_nombre: formData.proveedor_alquiler_nombre || '',
        seccion_catalogo: formData.seccion_catalogo || null,
        modelo_catalogo: formData.modelo_catalogo || null,
        medida: formData.medida || null,
        referencia_proveedor: formData.referencia_proveedor || null,
        lavado_aparte: !!formData.lavado_aparte,
        vuelve_si_sobra: !!formData.vuelve_si_sobra,
        es_caja: !!formData.es_caja,
        // Qué ingredientes del plato van dentro de la caja (vacío = todos)
        caja_ingredientes_ids: formData.es_caja ? (formData.caja_ingredientes_ids || []) : [],
        envase_uds: parseFloat(formData.envase_uds) || null,
        envase_entero: !!formData.envase_entero,
        envase_nombre: formData.envase_nombre || 'caja',
      };

      if (editingMaterial) {
        if (editingMaterial._esIngrediente) {
          // For ingredients shown in materials list, fetch current data and merge
          const currentResp = await ingredientesAPI.getOne(editingMaterial.id);
          const current = currentResp.data;
          const ingredienteUpdate = {
            nombre: cleanData.nombre,
            precio_unitario: current.precio_unitario ?? 0,
            unidad: current.unidad || 'kg',
            precio_paquete: current.precio_paquete ?? 0,
            unidades_por_paquete: current.unidades_por_paquete ?? 1,
            alergenos_ids: current.alergenos_ids || [],
            trazas_alergenos_ids: current.trazas_alergenos_ids || [],
            proveedor_id: cleanData.proveedor_id,
            activo: cleanData.activo,
            stock_minimo: cleanData.stock_minimo,
            carne: current.carne ?? false,
            origen_animal: current.origen_animal ?? false,
            pasteurizado: current.pasteurizado ?? false,
            sin_lactosa: current.sin_lactosa ?? false,
            contiene_cerdo: current.contiene_cerdo ?? false,
            no_apto_embarazadas: current.no_apto_embarazadas ?? false,
            revisado: current.revisado ?? false,
            tipo_iva: current.tipo_iva ?? 10,
          };
          await ingredientesAPI.update(editingMaterial.id, ingredienteUpdate);
        } else {
          await materialesAPI.update(editingMaterial.id, cleanData);
        }
      } else {
        await materialesAPI.create(cleanData);
      }
      fetchMateriales();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving material:', error);
      const detail = error?.response?.data?.detail;
      alert(detail ? `Error: ${detail}` : 'Error al guardar material');
    }
  };

  const handleDelete = async (id, esIngrediente = false) => {
    try {
      if (esIngrediente) {
        await ingredientesAPI.delete(id);
      } else {
        await materialesAPI.delete(id);
      }
      setDeleteConfirm(null);
      fetchMateriales();
    } catch (error) {
      console.error('Error deleting material:', error);
      alert('Error al eliminar material');
    }
  };

  const requestDelete = (material) => {
    setDeleteConfirm(material);
  };

  const handleOpenModal = (material = null) => {
    if (material) {
      setEditingMaterial(material);
      setFormData({
        nombre: material.nombre,
        categoria: material.categoria || 'menaje',
        tipo_coste: material.tipo_coste || 'inventariable',
        precio_unitario: material.precio_unitario || 0,
        precio_compra: material.precio_compra || 0,
        unidad_medida: material.unidad_medida || 'unidad',
        formato_unidad: material.formato_unidad || 'unidades',
        cantidad_por_envase: material.cantidad_por_envase || 1,
        precio_por_envase: material.precio_por_envase || 0,
        stock: material.stock || 0,
        stock_minimo: material.stock_minimo || 0,
        ubicacion: material.ubicacion || '',
        proveedor_id: material.proveedor_id || null,
        foto: material.foto || '',
        notas: material.notas || '',
        activo: material.activo,
        reutilizable: material.reutilizable !== false,
        es_caja: material.es_caja || false,
        caja_ingredientes_ids: Array.isArray(material.caja_ingredientes_ids) ? material.caja_ingredientes_ids : [],
        envase_uds: material.envase_uds || '',
        envase_entero: !!material.envase_entero,
        envase_nombre: material.envase_nombre || 'caja',
        precio_alquiler_dia1: material.precio_alquiler_dia1 || 0,
        precio_alquiler_dia_extra: material.precio_alquiler_dia_extra || 0,
        coste_limpieza: material.coste_limpieza || 0,
        proveedor_alquiler_nombre: material.proveedor_alquiler_nombre || '',
        seccion_catalogo: material.seccion_catalogo || '',
        modelo_catalogo: material.modelo_catalogo || '',
        medida: material.medida || '',
        referencia_proveedor: material.referencia_proveedor || '',
        lavado_aparte: !!material.lavado_aparte,
        vuelve_si_sobra: !!material.vuelve_si_sobra,
      });
    } else {
      setEditingMaterial(null);
      setFormData({
        nombre: '',
        categoria: 'menaje',
        tipo_coste: 'inventariable',
        precio_unitario: 0,
        precio_compra: 0,
        unidad_medida: 'unidad',
        formato_unidad: 'unidades',
        cantidad_por_envase: 1,
        precio_por_envase: 0,
        stock: 0,
        stock_minimo: 0,
        ubicacion: '',
        proveedor_id: null,
        foto: '',
        notas: '',
        activo: true,
        reutilizable: true,
        es_caja: false,
        caja_ingredientes_ids: [],
        envase_uds: '',
        envase_entero: false,
        envase_nombre: 'caja',
        precio_alquiler_dia1: 0,
        precio_alquiler_dia_extra: 0,
        coste_limpieza: 0,
        proveedor_alquiler_nombre: '',
        seccion_catalogo: '',
        modelo_catalogo: '',
        medida: '',
        referencia_proveedor: '',
        lavado_aparte: false,
        vuelve_si_sobra: false,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMaterial(null);
    setBusquedaCajaFiltro('');
  };

  // Marca o desmarca un ingrediente de los que van dentro de la caja preparada
  const toggleIngredienteCaja = (id) => {
    setFormData((prev) => {
      const actuales = prev.caja_ingredientes_ids || [];
      return {
        ...prev,
        caja_ingredientes_ids: actuales.includes(id) ? actuales.filter((x) => x !== id) : [...actuales, id],
      };
    });
  };

  // Ingredientes del buscador de la caja: los marcados siempre arriba
  const ingredientesCajaVisibles = (() => {
    const marcados = new Set(formData.caja_ingredientes_ids || []);
    const q = normalizarTexto(busquedaCajaFiltro.trim());
    return todosIngredientes
      .filter((ing) => marcados.has(ing.id) || !q || normalizarTexto(ing.nombre).includes(q))
      .sort((a, b) => (Number(marcados.has(b.id)) - Number(marcados.has(a.id)))
        || a.nombre.localeCompare(b.nombre, 'es'));
  })();

  const handleAddUbicacion = async () => {
    if (!nuevaUbicacion.trim()) {
      alert('Por favor ingresa un nombre para la ubicación');
      return;
    }
    try {
      await ubicacionesAPI.create({ nombre: nuevaUbicacion.trim() });
      await fetchUbicaciones();
      setFormData({ ...formData, ubicacion: nuevaUbicacion.trim() });
      setNuevaUbicacion('');
      setShowUbicacionModal(false);
    } catch (error) {
      console.error('Error creating ubicacion:', error);
      alert(error.response?.data?.detail || 'Error al crear ubicación');
    }
  };

  const handleDeleteUbicacion = async (ubicacionId, ubicacionNombre) => {
    if (window.confirm(`¿Estás seguro de eliminar la ubicación "${ubicacionNombre}"?`)) {
      try {
        await ubicacionesAPI.delete(ubicacionId);
        await fetchUbicaciones();
        // Si el material actual tenía esta ubicación, limpiarla
        if (formData.ubicacion === ubicacionNombre) {
          setFormData({ ...formData, ubicacion: '' });
        }
      } catch (error) {
        console.error('Error deleting ubicacion:', error);
        alert(error.response?.data?.detail || 'Error al eliminar ubicación');
      }
    }
  };

  // Calcular precio_unitario automáticamente para consumibles
  const calcularPrecioUnitario = (precio_envase, cantidad_envase) => {
    if (cantidad_envase > 0) {
      return parseFloat((precio_envase / cantidad_envase).toFixed(4));
    }
    return 0;
  };

  // Manejar cambios en los campos de envase para consumibles
  const handleEnvaseChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    
    if (field === 'precio_por_envase' || field === 'cantidad_por_envase') {
      const precio = field === 'precio_por_envase' ? value : formData.precio_por_envase;
      const cantidad = field === 'cantidad_por_envase' ? value : formData.cantidad_por_envase;
      newFormData.precio_unitario = calcularPrecioUnitario(precio, cantidad);
    }
    
    setFormData(newFormData);
  };

  const palabrasBusqueda = normalizarTexto(searchTerm).split(/\s+/).filter(Boolean);

  const filteredMateriales = materiales.filter(material => {
    const textos = CAMPOS_BUSQUEDA.map(campo => normalizarTexto(material[campo]));
    const matchesSearch = palabrasBusqueda.every(palabra => textos.some(t => t.includes(palabra)));
    const matchesTipo = !filterTipo || 
      (filterTipo === '__alquiler__' ? material.tipo_coste === 'alquiler' : material.tipo === filterTipo);
    const matchesActivo = filterActivo === '' || material.activo.toString() === filterActivo;
    const matchesSeccion = !filterSeccion || (material.tipo_coste === 'alquiler' && material.seccion_catalogo === filterSeccion);
    return matchesSearch && matchesTipo && matchesActivo && matchesSeccion;
  });

  if (filterTipo === '__alquiler__' && ordenAlquiler === 'mas_alquilados') {
    filteredMateriales.sort((a, b) =>
      ((usoAlquiler[b.id]?.eventos || 0) - (usoAlquiler[a.id]?.eventos || 0))
      || String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'));
  }

  const getTipoLabel = (tipo) => {
    const labels = {
      'desechable': 'Desechable',
      'menaje': 'Menaje de eventos',
      'utensilios': 'Utensilios de cocina'
    };
    return labels[tipo] || tipo;
  };

  const getTipoBadge = (tipo) => {
    const badges = {
      'desechable': 'bg-red-100 text-red-800',
      'menaje': 'bg-blue-100 text-blue-800',
      'utensilios': 'bg-green-100 text-green-800'
    };
    return badges[tipo] || 'bg-gray-100 text-gray-800';
  };

  const getTipoCosteBadge = (tipo_coste) => {
    if (tipo_coste === 'alquiler') return 'bg-purple-100 text-purple-800 border border-purple-300';
    return '';
  };

  const [activeTab, setActiveTab] = useState('materiales');

  if (loading && activeTab === 'materiales') {
    return <div className="flex items-center justify-center h-64">Cargando...</div>;
  }

  return (
    <div>
      {/* Header con Tabs */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Materiales y Vehículos</h1>
        <p className="text-gray-600 mt-1">Gestión de inventario y flota de vehículos</p>
        
        {/* Tabs */}
        <div className="flex space-x-1 mt-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('materiales')}
            className={`flex items-center px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${
              activeTab === 'materiales'
                ? 'bg-white text-duetto-primary border-t border-l border-r border-gray-200 -mb-px'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Package className="w-4 h-4 mr-2" />
            Materiales
          </button>
          <button
            onClick={() => setActiveTab('vehiculos')}
            className={`flex items-center px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${
              activeTab === 'vehiculos'
                ? 'bg-white text-duetto-primary border-t border-l border-r border-gray-200 -mb-px'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Truck className="w-4 h-4 mr-2" />
            Vehículos
          </button>
        </div>
      </div>

      {/* Contenido según tab activa */}
      {activeTab === 'vehiculos' ? (
        <Vehiculos />
      ) : (
        <>
          {/* Header de Materiales */}
          <div className="flex justify-end mb-6 gap-3">
            <button
              onClick={async () => {
                try {
                  const response = await materialesAPI.notificarDeficit(7);
                  const data = response.data;
                  if (data.alertas === 0) {
                    alert('No hay déficits de material para los próximos 7 días.');
                  } else {
                    alert(`Notificación enviada a todos: ${data.alertas} materiales con déficit en ${data.dias_con_deficit} día(s).`);
                  }
                } catch (error) {
                  console.error('Error:', error);
                  alert('Error al enviar notificación');
                }
              }}
              data-testid="notificar-deficit-btn"
              className="bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 transition flex items-center space-x-2"
            >
              <Bell className="w-5 h-5" />
              <span>Notificar Déficit</span>
            </button>
            <button
              onClick={() => handleOpenModal()}
              className="bg-duetto-primary text-white px-4 py-2 rounded-lg hover:bg-opacity-90 transition flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Nuevo Material</span>
            </button>
          </div>

          {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Materiales</p>
              <p className="text-2xl font-bold text-gray-900">{materiales.length}</p>
            </div>
            <Package className="w-8 h-8 text-duetto-primary opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Desechables</p>
              <p className="text-2xl font-bold text-red-600">
                {materiales.filter(m => m.tipo === 'desechable').length}
              </p>
            </div>
            <Package className="w-8 h-8 text-red-400 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Menaje</p>
              <p className="text-2xl font-bold text-blue-600">
                {materiales.filter(m => m.tipo === 'menaje').length}
              </p>
            </div>
            <Package className="w-8 h-8 text-blue-400 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Utensilios</p>
              <p className="text-2xl font-bold text-green-600">
                {materiales.filter(m => m.tipo === 'utensilios').length}
              </p>
            </div>
            <Package className="w-8 h-8 text-green-400 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-purple-300 p-4" data-testid="stats-alquiler">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-700 font-medium">Alquiler</p>
              <p className="text-2xl font-bold text-purple-600">
                {materiales.filter(m => m.tipo_coste === 'alquiler').length}
              </p>
            </div>
            <Truck className="w-8 h-8 text-purple-400 opacity-50" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className={`grid grid-cols-1 gap-4 ${filterTipo === '__alquiler__' ? 'md:grid-cols-5' : 'md:grid-cols-3'}`}>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Search className="w-4 h-4 inline mr-2" />
              Buscar
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nombre, modelo, medida, referencia..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
            <select
              value={filterTipo}
              onChange={(e) => { setFilterTipo(e.target.value); if (e.target.value !== '__alquiler__') setFilterSeccion(''); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
            >
              <option value="">Todos los tipos</option>
              <option value="desechable">Desechable</option>
              <option value="menaje">Menaje de eventos</option>
              <option value="utensilios">Utensilios de cocina</option>
              <option value="__alquiler__">Solo Alquiler</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
            <select
              value={filterActivo}
              onChange={(e) => setFilterActivo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
            >
              <option value="">Todos</option>
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
            </select>
          </div>
          {filterTipo === '__alquiler__' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sección de alquiler</label>
              <select
                value={filterSeccion}
                onChange={(e) => setFilterSeccion(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                data-testid="filtro-seccion-alquiler"
              >
                <option value="">Todas las secciones</option>
                {SECCIONES_ALQUILER.map(sec => <option key={sec} value={sec}>{sec}</option>)}
              </select>
            </div>
          )}
          {filterTipo === '__alquiler__' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Orden</label>
              <select
                value={ordenAlquiler}
                onChange={(e) => setOrdenAlquiler(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                data-testid="orden-alquiler"
              >
                <option value="">Por nombre</option>
                <option value="mas_alquilados">Más alquilados</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Materials List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Material</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pico Demanda 7d</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ubicación</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredMateriales.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                    No se encontraron materiales
                  </td>
                </tr>
              ) : (
                filteredMateriales.map((material) => (
                  <tr key={material.id} className={`hover:bg-gray-50 transition ${material._sinCategoria ? 'bg-red-50' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                          <Package className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <p className={`font-medium ${material._sinCategoria ? 'text-red-600' : 'text-gray-900'}`}>
                            {material.nombre}
                            {material._sinCategoria && <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Sin Categoría</span>}
                            {material._esIngrediente && !material._sinCategoria && <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Menaje</span>}
                            {material.es_caja && <span className="ml-2 px-1.5 py-px rounded text-[11px] font-bold text-white bg-[#C2870A]" data-testid="badge-caja">CAJA</span>}
                            {!material._esIngrediente && textoEnvase(material) && <span className="ml-2 text-[11px] font-normal text-[#5B6B64]" data-testid="texto-envase">{textoEnvase(material)}</span>}
                            {material.tipo_coste === 'alquiler' && <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full border border-purple-300 font-semibold" data-testid="badge-alquiler">ALQUILER</span>}
                          </p>
                          {material.tipo_coste === 'alquiler' && (material.proveedor_alquiler_nombre || material.seccion_catalogo || material.medida) && (
                            <p className="text-xs text-purple-600 mt-0.5">
                              {[
                                material.seccion_catalogo,
                                material.medida,
                                material.proveedor_alquiler_nombre,
                                `€${material.precio_alquiler_dia1 || 0}/día`,
                              ].filter(Boolean).join(' · ')}
                            </p>
                          )}
                          {material.tipo_coste === 'alquiler' && (() => {
                            const uso = usoAlquiler[material.id];
                            const veces = uso?.eventos || 0;
                            return (
                              <p className="text-xs mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid="uso-alquiler">
                                <span className={`px-1.5 py-0.5 rounded ${veces > 0 ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-gray-100 text-gray-500'}`}>
                                  {veces > 0 ? `Alquilado ${veces} ${veces === 1 ? 'vez' : 'veces'}` : 'Sin alquilar'}
                                </span>
                                {veces > 0 && uso.ultimo && (
                                  <span className="text-gray-500">
                                    último {fechaCorta(uso.ultimo)}{uso.ultimo_cliente ? ` · ${uso.ultimo_cliente}` : ''}
                                  </span>
                                )}
                              </p>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTipoBadge(material.tipo)}`}>
                        {getTipoLabel(material.tipo)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-900">{material.stock || 0}</td>
                    <td className="px-6 py-4">
                      {disponibilidadSemana[material.id] ? (
                        (() => {
                          const disp = disponibilidadSemana[material.id];
                          if (!disp.pico_demanda || disp.pico_demanda === 0) {
                            return <span className="text-gray-400 text-xs">Sin demanda</span>;
                          }
                          const fechaPico = disp.pico_fecha ? new Date(disp.pico_fecha + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) : '';
                          const eventos = disp.pico_eventos || [];
                          return (
                            <div className="relative group cursor-pointer">
                              <div className={`font-semibold ${disp.deficit > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                {Math.round(disp.pico_demanda)} {disp.unidad || 'ud'}
                              </div>
                              <div className="text-xs text-gray-500">{fechaPico}</div>
                              {disp.deficit > 0 && (
                                <div className="text-xs text-red-600 font-medium">
                                  Faltan {Math.round(disp.deficit)}
                                </div>
                              )}
                              {/* Tooltip con desglose por evento */}
                              {eventos.length > 0 && (
                                <div className="absolute z-50 hidden group-hover:block top-full left-0 mt-2 w-80 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3">
                                  <div className="absolute top-0 left-8 -translate-y-full w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-transparent border-b-gray-900"></div>
                                  <div className="font-bold mb-2 text-amber-300 border-b border-gray-700 pb-1">
                                    Desglose {fechaPico}
                                  </div>
                                  {eventos.map((ev, idx) => {
                                    const clienteCorto = ev.cliente ? (ev.cliente.length > 10 ? ev.cliente.substring(0, 10) + '...' : ev.cliente) : '';
                                    return (
                                      <div key={idx} className="py-1.5 border-b border-gray-700 last:border-0">
                                        <div className="flex justify-between items-start">
                                          <div className="flex-1 min-w-0">
                                            <a
                                              href={`/presupuestos/${ev.presupuesto_id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="font-semibold text-blue-300 hover:text-blue-200 hover:underline cursor-pointer"
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              #{ev.numero}
                                            </a>
                                            {clienteCorto && <span className="text-gray-300 ml-1">{clienteCorto}</span>}
                                          </div>
                                          <span className="font-bold text-amber-300 ml-2 whitespace-nowrap">{Math.round(ev.cantidad)} {disp.unidad || 'ud'}</span>
                                        </div>
                                        {ev.menus && ev.menus.length > 0 && (
                                          <div className="mt-0.5 pl-2">
                                            {ev.menus.map((menu, midx) => (
                                              <div key={midx} className="text-gray-400 text-[10px] flex justify-between">
                                                <span className="truncate max-w-[180px]">{menu.nombre}</span>
                                                <span className="text-amber-200 ml-1 whitespace-nowrap">{Math.round(menu.cantidad_material)} ud</span>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                  <div className="flex justify-between mt-2 pt-1 border-t border-gray-600 font-bold">
                                    <span>Total día</span>
                                    <span className={disp.deficit > 0 ? 'text-red-400' : 'text-green-400'}>
                                      {Math.round(disp.pico_demanda)} / {Math.round(disp.stock)} stock
                                    </span>
                                  </div>
                                  {disp.deficit > 0 && (
                                    <div className="text-red-400 font-bold text-center mt-1">
                                      Faltan {Math.round(disp.deficit)} {disp.unidad || 'ud'}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-600">{material.ubicacion || '-'}</td>
                    <td className="px-6 py-4 text-gray-600">
                      {material.proveedor_id ? (
                        (() => {
                          const proveedor = proveedores.find(p => p.id === material.proveedor_id);
                          return proveedor ? proveedor.nombre : 'Proveedor no encontrado';
                        })()
                      ) : (
                        <span className="text-gray-400 text-xs">Sin proveedor</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {material.activo ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                          Activo
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-semibold rounded-full">
                          Inactivo
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleOpenModal(material)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => requestDelete(material)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingMaterial ? 'Editar Material' : 'Nuevo Material'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Nombre */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre del Material *
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary"
                  required
                  placeholder="Ej: Film plástico 300m"
                />
              </div>

              {/* Categoría y Tipo de Coste */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoría *
                  </label>
                  <select
                    value={formData.categoria}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    required
                  >
                    <option value="menaje">Menaje</option>
                    <option value="desechable">Desechable</option>
                    <option value="textil">Textil</option>
                    <option value="mobiliario">Mobiliario</option>
                    <option value="utensilios">Utensilios</option>
                    <option value="decoracion">Decoración</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Coste *
                  </label>
                  <select
                    value={formData.tipo_coste}
                    onChange={(e) => setFormData({ ...formData, tipo_coste: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    required
                  >
                    <option value="consumible">Consumible (film, papel)</option>
                    <option value="inventariable">Inventariable (sillas, bandejas)</option>
                    <option value="alquiler">Alquiler (proveedor externo)</option>
                  </select>
                </div>
              </div>

              {/* Ayuda sobre tipo de coste */}
              <div className={`border rounded-lg p-3 ${formData.tipo_coste === 'alquiler' ? 'bg-purple-50 border-purple-200' : 'bg-blue-50 border-blue-200'}`}>
                <p className={`text-xs ${formData.tipo_coste === 'alquiler' ? 'text-purple-800' : 'text-blue-800'}`}>
                  <strong>
                    {formData.tipo_coste === 'consumible' ? 'Consumible:' : 
                     formData.tipo_coste === 'alquiler' ? 'Alquiler:' : 'Inventariable:'}
                  </strong>
                  {formData.tipo_coste === 'consumible' 
                    ? ' Se descuenta del stock y tiene coste por uso (ej: film, papel horno)'
                    : formData.tipo_coste === 'alquiler'
                    ? ' Material de proveedor externo. Se alquila por evento con precio por día'
                    : ' Tiene valor de compra pero no coste por uso. Ya está amortizado (ej: sillas, bandejas)'
                  }
                </p>
              </div>

              {/* Coste por uso (solo para consumibles) */}
              {formData.tipo_coste === 'consumible' && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-800">Configuración de Consumible</h3>
                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={!!formData.vuelve_si_sobra}
                      onChange={(e) => setFormData({ ...formData, vuelve_si_sobra: e.target.checked })}
                      className="w-4 h-4 mt-0.5"
                      data-testid="material-vuelve-si-sobra"
                    />
                    <span>
                      Vuelve lo que sobra
                      <span className="block text-xs text-gray-500">
                        Servilletas, guantes, vasos...: en el coste del evento cuenta lo que se consume, no lo que se lleva.
                        Sin marcar (bolsas de basura, film), lo que se lleva se gasta.
                      </span>
                    </span>
                  </label>
                  
                  {/* Formato unidad */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Formato/Unidad *
                    </label>
                    <select
                      value={formData.formato_unidad}
                      onChange={(e) => setFormData({ ...formData, formato_unidad: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    >
                      <option value="unidades">Unidades</option>
                      <option value="metros">Metros</option>
                    </select>
                  </div>

                  {/* Cantidad por envase y precio por envase */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cantidad por Envase *
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        value={formData.cantidad_por_envase}
                        onChange={(e) => handleEnvaseChange('cantidad_por_envase', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        onBlur={(e) => { if (e.target.value === '') handleEnvaseChange('cantidad_por_envase', 1); }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary"
                        min="0.1"
                        placeholder="100"
                        required
                      />
                      <p className="text-xs text-gray-600 mt-1">
                        {formData.formato_unidad === 'metros' ? 'metros por rollo' : 'unidades por paquete'}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Precio por Envase *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">€</span>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.precio_por_envase}
                          onChange={(e) => handleEnvaseChange('precio_por_envase', e.target.value === '' ? '' : parseFloat(e.target.value))}
                          onBlur={(e) => { if (e.target.value === '') handleEnvaseChange('precio_por_envase', 0); }}
                          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary"
                          min="0"
                          placeholder="4.00"
                          required
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-1">Precio total del envase</p>
                    </div>
                  </div>

                  {/* Coste calculado por unidad */}
                  <div className="bg-white p-3 rounded border border-yellow-300">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">Coste por {formData.formato_unidad === 'metros' ? 'metro' : 'unidad'}:</span>
                      <span className="text-lg font-bold text-duetto-primary">
                        €{typeof formData.precio_unitario === 'number' ? formData.precio_unitario.toFixed(4) : '0.0000'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Cálculo: €{formData.precio_por_envase || 0} ÷ {formData.cantidad_por_envase || 1} {formData.formato_unidad}
                    </p>
                  </div>
                </div>
              )}

              {/* Valor de compra (solo para inventariables) */}
              {formData.tipo_coste === 'inventariable' && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valor de Compra (para valoración de inventario)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">€</span>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.precio_compra}
                      onChange={(e) => setFormData({ ...formData, precio_compra: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                      onBlur={(e) => { if (e.target.value === '') setFormData({...formData, precio_compra: 0}); }}
                      className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary"
                      min="0"
                      placeholder="0.00"
                    />
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Valor unitario original de compra</p>
                </div>
              )}

              {/* Configuración de Alquiler */}
              {formData.tipo_coste === 'alquiler' && (
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 space-y-3" data-testid="alquiler-config">
                  <h3 className="text-sm font-semibold text-purple-900">Configuración de Alquiler</h3>
                  
                  {/* Proveedor de alquiler: ficha de proveedor y, de respaldo, el nombre en texto */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Proveedor de alquiler
                      </label>
                      <select
                        value={formData.proveedor_id || ''}
                        onChange={(e) => {
                          const idProv = e.target.value || null;
                          const prov = proveedores.find(p => p.id === idProv);
                          setFormData({
                            ...formData,
                            proveedor_id: idProv,
                            proveedor_alquiler_nombre: prov ? prov.nombre : formData.proveedor_alquiler_nombre,
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        data-testid="alquiler-proveedor-id"
                      >
                        <option value="">Sin ficha de proveedor</option>
                        {[...proveedores].sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '')).map((prov) => (
                          <option key={prov.id} value={prov.id}>{prov.nombre}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre del proveedor (texto)
                      </label>
                      <input
                        type="text"
                        value={formData.proveedor_alquiler_nombre}
                        onChange={(e) => setFormData({ ...formData, proveedor_alquiler_nombre: e.target.value })}
                        placeholder="Si no tiene ficha de proveedor..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  {/* Datos del catálogo del proveedor */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sección</label>
                      <select
                        value={formData.seccion_catalogo || ''}
                        onChange={(e) => setFormData({ ...formData, seccion_catalogo: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        data-testid="alquiler-seccion"
                      >
                        <option value="">Sin sección</option>
                        {SECCIONES_ALQUILER.map(sec => <option key={sec} value={sec}>{sec}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
                      <input
                        type="text"
                        value={formData.modelo_catalogo || ''}
                        onChange={(e) => setFormData({ ...formData, modelo_catalogo: e.target.value })}
                        placeholder="Colección o modelo del catálogo"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        data-testid="alquiler-modelo"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Medida</label>
                      <input
                        type="text"
                        value={formData.medida || ''}
                        onChange={(e) => setFormData({ ...formData, medida: e.target.value })}
                        placeholder="Ej.: 28 cm, 180x75"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        data-testid="alquiler-medida"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Referencia del proveedor</label>
                      <input
                        type="text"
                        value={formData.referencia_proveedor || ''}
                        onChange={(e) => setFormData({ ...formData, referencia_proveedor: e.target.value })}
                        placeholder="Código del artículo en su catálogo"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        data-testid="alquiler-referencia"
                      />
                    </div>
                  </div>

                  {/* Precios de alquiler */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Precio 1er día (por ud.) *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">€</span>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.precio_alquiler_dia1}
                          onChange={(e) => setFormData({ ...formData, precio_alquiler_dia1: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                          onBlur={(e) => { if (e.target.value === '') setFormData({...formData, precio_alquiler_dia1: 0}); }}
                          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          min="0"
                          placeholder="5.00"
                          data-testid="precio-alquiler-dia1"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Precio día extra (por ud.)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-gray-500">€</span>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.precio_alquiler_dia_extra}
                          onChange={(e) => setFormData({ ...formData, precio_alquiler_dia_extra: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                          onBlur={(e) => { if (e.target.value === '') setFormData({...formData, precio_alquiler_dia_extra: 0}); }}
                          className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          min="0"
                          placeholder="3.00"
                          data-testid="precio-alquiler-dia-extra"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Precio por cada día adicional</p>
                    </div>
                  </div>

                  {/* Coste de limpieza */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Coste de Limpieza (por ud.)
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">€</span>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.coste_limpieza}
                        onChange={(e) => setFormData({ ...formData, coste_limpieza: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                        onBlur={(e) => { if (e.target.value === '') setFormData({...formData, coste_limpieza: 0}); }}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        min="0"
                        placeholder="0.50"
                        data-testid="coste-limpieza"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Coste de limpieza por unidad (si aplica)</p>
                    <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={!!formData.lavado_aparte}
                        onChange={(e) => setFormData({ ...formData, lavado_aparte: e.target.checked })}
                        className="w-4 h-4"
                        data-testid="alquiler-lavado-aparte"
                      />
                      Lavado aparte (el proveedor cobra el lavado por pieza: es el coste de limpieza)
                    </label>
                  </div>

                  {/* Ejemplo de cálculo */}
                  {formData.precio_alquiler_dia1 > 0 && (
                    <div className="bg-white p-3 rounded border border-purple-300">
                      <p className="text-xs font-semibold text-purple-800 mb-1">Ejemplo de coste por 10 uds:</p>
                      <div className="text-xs text-gray-700 space-y-0.5">
                        <p>1 día: <strong>€{(10 * formData.precio_alquiler_dia1).toFixed(2)}</strong></p>
                        <p>2 días: <strong>€{(10 * (formData.precio_alquiler_dia1 + (parseFloat(formData.precio_alquiler_dia_extra) || 0))).toFixed(2)}</strong></p>
                        {formData.coste_limpieza > 0 && (
                          <p>+ Limpieza: <strong>€{(10 * formData.coste_limpieza).toFixed(2)}</strong></p>
                        )}
                      </div>
                      <p className="text-xs text-purple-600 mt-1 italic">* Transporte y fianza se configuran por evento en el presupuesto</p>
                    </div>
                  )}
                </div>
              )}

              {/* Stock y Stock Mínimo (no para alquiler) */}
              {formData.tipo_coste !== 'alquiler' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stock Actual
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                    onBlur={(e) => { if (e.target.value === '') setFormData({...formData, stock: 0}); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Stock Mínimo (alerta)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.stock_minimo}
                    onChange={(e) => setFormData({ ...formData, stock_minimo: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                    onBlur={(e) => { if (e.target.value === '') setFormData({...formData, stock_minimo: 0}); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary"
                    min="0"
                  />
                </div>
              </div>
              )}

              {/* Alerta de Stock Bajo */}
              {formData.tipo_coste !== 'alquiler' && formData.stock_minimo > 0 && formData.stock <= formData.stock_minimo && (
                <div className="bg-orange-50 border border-orange-300 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <span className="text-orange-600 text-lg">⚠️</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-orange-800">Stock por debajo del mínimo</p>
                      <p className="text-xs text-orange-700 mt-1">
                        Stock actual: {formData.stock} | Stock mínimo: {formData.stock_minimo}
                      </p>
                      {formData.proveedor_id && (
                        <p className="text-xs text-orange-700 mt-1">
                          💼 Proveedor: <strong>{proveedores.find(p => p.id === formData.proveedor_id)?.nombre || 'Desconocido'}</strong>
                        </p>
                      )}
                      {!formData.proveedor_id && (
                        <p className="text-xs text-orange-700 mt-1">
                          ⚠️ No hay proveedor asignado para reposición
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Valoración del Stock */}
              {formData.stock > 0 && formData.tipo_coste !== 'alquiler' && (
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Valoración del stock:</span>
                    <span className="font-bold text-gray-900">
                      €{(formData.stock * (formData.tipo_coste === 'consumible' ? (parseFloat(formData.precio_unitario) || 0) : (parseFloat(formData.precio_compra) || 0))).toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.stock} {formData.unidad_medida} × €{formData.tipo_coste === 'consumible' ? (parseFloat(formData.precio_unitario) || 0).toFixed(2) : (parseFloat(formData.precio_compra) || 0).toFixed(2)}
                  </p>
                </div>
              )}

              {/* Ubicación con dropdown y gestión */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ubicación
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.ubicacion}
                    onChange={(e) => {
                      if (e.target.value === '__add_new__') {
                        setShowUbicacionModal(true);
                      } else {
                        setFormData({ ...formData, ubicacion: e.target.value });
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary"
                  >
                    <option value="">Sin ubicación</option>
                    {[...ubicaciones].sort((a, b) => a.nombre.localeCompare(b.nombre)).map((ub) => (
                      <option key={ub.id} value={ub.nombre}>
                        {ub.nombre}
                      </option>
                    ))}
                    <option value="__add_new__">➕ Añadir nueva ubicación...</option>
                  </select>
                  {formData.ubicacion && ubicaciones.find(u => u.nombre === formData.ubicacion) && (
                    <button
                      type="button"
                      onClick={() => {
                        const ub = ubicaciones.find(u => u.nombre === formData.ubicacion);
                        if (ub) handleDeleteUbicacion(ub.id, ub.nombre);
                      }}
                      className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition border border-red-300"
                      title="Eliminar ubicación"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Proveedor (en alquiler va en su bloque) */}
              {formData.tipo_coste !== 'alquiler' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Proveedor
                </label>
                <select
                  value={formData.proveedor_id || ''}
                  onChange={(e) => setFormData({ ...formData, proveedor_id: e.target.value || null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary"
                >
                  <option value="">Sin proveedor asignado</option>
                  {[...proveedores].sort((a, b) => a.nombre.localeCompare(b.nombre)).map((prov) => (
                    <option key={prov.id} value={prov.id}>
                      {prov.nombre}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Selecciona el proveedor para reposición de stock
                </p>
              </div>
              )}

              {/* Notas */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notas
                </label>
                <textarea
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  placeholder="Información adicional sobre el material..."
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary"
                />
              </div>

              {/* Activo */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="activo"
                  checked={formData.activo}
                  onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                  className="w-4 h-4 text-duetto-primary rounded focus:ring-2 focus:ring-duetto-primary"
                />
                <label htmlFor="activo" className="ml-2 text-sm text-gray-700">
                  Material activo
                </label>
              </div>

              {/* Reutilizable entre servicios */}
              <div className="flex items-center bg-blue-50 p-3 rounded-lg border border-blue-200">
                <input
                  type="checkbox"
                  id="reutilizable"
                  checked={formData.reutilizable}
                  onChange={(e) => setFormData({ ...formData, reutilizable: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <label htmlFor="reutilizable" className="ml-2 text-sm text-gray-700">
                  <span className="font-medium">Reutilizable entre servicios</span>
                  <span className="block text-xs text-gray-500">Si está activo, cuando hay varios servicios en el mismo evento que no se solapan en horario, se cuenta el máximo necesario en vez de sumar todos</span>
                </label>
              </div>

              {/* Caja preparada: va llena con los consumibles del servicio */}
              <div className="flex items-center bg-amber-50 p-3 rounded-lg border border-amber-200">
                <input
                  type="checkbox"
                  id="es_caja"
                  data-testid="material-es-caja"
                  checked={!!formData.es_caja}
                  onChange={(e) => setFormData({ ...formData, es_caja: e.target.checked })}
                  className="w-4 h-4 text-amber-600 rounded focus:ring-2 focus:ring-amber-500"
                />
                <label htmlFor="es_caja" className="ml-2 text-sm text-gray-700">
                  <span className="font-medium">Caja preparada</span>
                  <span className="block text-xs text-gray-500">Va llena con los consumibles del servicio al que se asigna (azucarillos, tés...). La hoja de material y la pestaña Material enseñan la caja con su contenido y no listan los sobres sueltos. Compras sigue reponiendo los sobres.</span>
                </label>
              </div>
              {formData.es_caja && (
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 -mt-2" data-testid="material-caja-filtro">
                  <p className="text-sm font-medium text-gray-700">Qué va dentro</p>
                  <p className="text-xs text-gray-500 mb-2">Vacío = todos los ingredientes del plato al que se asigna. Marca solo algunos si el resto va suelto (el balde de barra libre lleva las cervezas, no el vino ni los refrescos).</p>
                  <input
                    type="text"
                    value={busquedaCajaFiltro}
                    onChange={(e) => setBusquedaCajaFiltro(e.target.value)}
                    placeholder="Buscar ingrediente..."
                    data-testid="material-caja-filtro-buscar"
                    className="w-full px-3 py-1.5 mb-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                  <div className="max-h-40 overflow-y-auto bg-white border border-amber-100 rounded">
                    {ingredientesCajaVisibles.length === 0 ? (
                      <p className="px-2 py-1.5 text-xs text-gray-500">No hay ingredientes que coincidan.</p>
                    ) : ingredientesCajaVisibles.map((ing) => (
                      <label key={ing.id} className="flex items-center gap-2 px-2 py-1 text-sm text-gray-700 hover:bg-amber-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(formData.caja_ingredientes_ids || []).includes(ing.id)}
                          onChange={() => toggleIngredienteCaja(ing.id)}
                          data-testid={`material-caja-filtro-${ing.id}`}
                          className="w-4 h-4 text-amber-600 rounded focus:ring-2 focus:ring-amber-500"
                        />
                        <span className="flex-1">{ing.nombre}</span>
                        {ing.unidad && <span className="text-xs text-gray-400">{ing.unidad}</span>}
                      </label>
                    ))}
                  </div>
                  {(formData.caja_ingredientes_ids || []).length > 0 && (
                    <p className="text-xs text-gray-600 mt-1">{formData.caja_ingredientes_ids.length} {formData.caja_ingredientes_ids.length === 1 ? 'ingrediente marcado' : 'ingredientes marcados'}. El resto del plato va suelto.</p>
                  )}
                </div>
              )}

              {/* Envase de carga: rack de copas, caja de servilletas... */}
              {!(editingMaterial && editingMaterial._esIngrediente) && (
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200" data-testid="material-envase">
                  <p className="text-sm font-medium text-gray-700 mb-2">Envase de carga</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="envase_uds" className="block text-xs text-gray-600 mb-1">Uds por rack o caja</label>
                      <input
                        type="number"
                        id="envase_uds"
                        min="0"
                        step="1"
                        value={formData.envase_uds}
                        onChange={(e) => setFormData({ ...formData, envase_uds: e.target.value })}
                        placeholder="Sin envase"
                        data-testid="material-envase-uds"
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="envase_nombre" className="block text-xs text-gray-600 mb-1">Nombre</label>
                      <select
                        id="envase_nombre"
                        value={formData.envase_nombre || 'caja'}
                        onChange={(e) => setFormData({ ...formData, envase_nombre: e.target.value })}
                        data-testid="material-envase-nombre"
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                      >
                        {NOMBRES_ENVASE.map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="flex items-start mt-2">
                    <input
                      type="checkbox"
                      id="envase_entero"
                      checked={!!formData.envase_entero}
                      onChange={(e) => setFormData({ ...formData, envase_entero: e.target.checked })}
                      data-testid="material-envase-entero"
                      className="w-4 h-4 mt-0.5 text-duetto-primary rounded focus:ring-2 focus:ring-duetto-primary"
                    />
                    <label htmlFor="envase_entero" className="ml-2 text-sm text-gray-700">
                      <span className="font-medium">Va entero</span>
                      <span className="block text-xs text-gray-500">Copas y vasos: se cargan racks completos. Sin marcar, la hoja dice cuántos envases y cuántas sueltas.</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Botones */}
              <div className="flex space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-duetto-primary text-white rounded-lg hover:bg-opacity-90"
                >
                  {editingMaterial ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal para añadir nueva ubicación */}
      {showUbicacionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">Nueva Ubicación</h3>
              <button 
                onClick={() => {
                  setShowUbicacionModal(false);
                  setNuevaUbicacion('');
                }} 
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre de la Ubicación *
                </label>
                <input
                  type="text"
                  value={nuevaUbicacion}
                  onChange={(e) => setNuevaUbicacion(e.target.value)}
                  placeholder="Ej: Almacén A - Estantería 3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-duetto-primary"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddUbicacion();
                    }
                  }}
                  autoFocus
                />
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowUbicacionModal(false);
                    setNuevaUbicacion('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleAddUbicacion}
                  className="flex-1 px-4 py-2 bg-duetto-primary text-white rounded-lg hover:bg-opacity-90"
                >
                  Crear
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de borrado */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Confirmar eliminación</h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                Estás a punto de eliminar permanentemente:
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="font-semibold text-red-800" data-testid="delete-confirm-name">{deleteConfirm.nombre}</p>
                {deleteConfirm.categoria && (
                  <p className="text-xs text-red-600 mt-1">Categoría: {deleteConfirm.categoria}</p>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Esta acción quedará registrada en el log de auditoría y no se puede deshacer.
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  data-testid="delete-cancel-btn"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(deleteConfirm.id, deleteConfirm._esIngrediente)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                  data-testid="delete-confirm-btn"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default Materiales;
